import React, { useEffect, useMemo, useState } from "react";
import { ArrowRight, Briefcase, Calendar, CheckCircle2, FolderOpen, MapPin, Table2, Users, UtensilsCrossed } from "lucide-react";

import collaboratorApi from "../../../lib/collaboratorApi";
import { Card, EmptyState, Pill, SkeletonBlock, StatCard, TabSkeleton } from "./ui";
import { computeGuestStats } from "../guestStats";

const STATUS = {
  todo: { label: "À faire", tone: "neutral" },
  "in-progress": { label: "En cours", tone: "info" },
  validation_pending: { label: "À valider", tone: "warning" },
  done: { label: "Terminé", tone: "success" },
};
const PRIORITY_TONE = { Haute: "danger", Moyenne: "warning", Basse: "neutral" };

/** Whole days from today to an ISO date (negative once it has passed), or null. */
export function daysUntil(isoDate, now = new Date()) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(isoDate || ""))) return null;
  const [year, month, day] = isoDate.split("-").map(Number);
  const target = Date.UTC(year, month - 1, day);
  const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((target - today) / 86400000);
}

export function countdownLabel(days) {
  if (days === null) return { big: "—", small: "Date à venir" };
  if (days === 0) return { big: "Aujourd'hui", small: "C'est le grand jour" };
  if (days === 1) return { big: "Demain", small: "Dernière ligne droite" };
  if (days > 1) return { big: `J-${days}`, small: `${days} jours restants` };
  return { big: "Passé", small: `il y a ${Math.abs(days)} jour${Math.abs(days) > 1 ? "s" : ""}` };
}

const formatLongDate = (isoDate) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(isoDate || ""))) return "Date à venir";
  return new Intl.DateTimeFormat("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" }).format(new Date(`${isoDate}T12:00:00`));
};

const SHORTCUTS = [
  { id: "timeline", label: "Planning", text: "Le déroulé de la journée", icon: Calendar, key: "timeline" },
  { id: "mission", label: "Missions", text: "Ce qu'il y a à faire", icon: Briefcase, key: "mission" },
  { id: "tables", label: "Plan de table", text: "La salle et les tables", icon: Table2, key: "tables" },
  { id: "guests", label: "Invités", text: "Suivi des réponses", icon: Users, key: "guests" },
  { id: "dietary", label: "Restrictions", text: "Régimes et allergies", icon: UtensilsCrossed, key: "dietary_restrictions" },
  { id: "documents", label: "Documents", text: "Contrats, plans, devis", icon: FolderOpen, key: "documents" },
];

export default function OverviewTab({ permissions = {}, onNavigate = () => {} }) {
  const [data, setData] = useState(null);
  const [guests, setGuests] = useState(null);
  const [error, setError] = useState("");
  const canSeeGuests = Boolean(permissions.guests);

  useEffect(() => {
    let ignore = false;
    const fetchOverview = () => {
      collaboratorApi
        .get("/collaborator-portal/overview")
        .then((response) => { if (!ignore) setData(response.data); })
        .catch(() => { if (!ignore) setError("Impossible de charger l’aperçu collaborateur."); });
    };
    fetchOverview();
    const intervalId = setInterval(fetchOverview, 10000);
    return () => { ignore = true; clearInterval(intervalId); };
  }, []);

  useEffect(() => {
    if (!canSeeGuests) return undefined;
    let ignore = false;
    collaboratorApi
      .get("/collaborator-portal/guests")
      .then((response) => { if (!ignore) setGuests(response.data || []); })
      .catch(() => { /* the figure simply stays hidden */ });
    return () => { ignore = true; };
  }, [canSeeGuests]);

  const guestStats = useMemo(() => (guests ? computeGuestStats(guests) : null), [guests]);

  if (error) {
    return <div role="alert" className="w-full rounded-2xl border border-red-100 bg-red-50 p-6 text-sm text-red-700">{error}</div>;
  }
  if (!data) return <TabSkeleton />;

  const couple = [data.wedding?.bride_name, data.wedding?.groom_name].filter(Boolean).join(" & ") || "Mariage Ceremind";
  const days = daysUntil(data.wedding?.date);
  const countdown = countdownLabel(days);
  const tasks = data.urgent_tasks || [];
  const events = data.next_events || [];
  const shortcuts = SHORTCUTS.filter((entry) => permissions[entry.key]);

  return (
    <div className="w-full space-y-6 pb-8">
      <div className="overflow-hidden rounded-3xl border border-[#e9e1d4] bg-gradient-to-br from-[#f7f1e6] via-white to-[#eef3ec] p-6 shadow-[0_18px_40px_-34px_rgba(83,64,52,0.5)] md:p-10">
        <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
          <div className="min-w-0">
            <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#5f7d68]">Bienvenue, {data.collaborator?.name?.split(" ")[0] || "vous"}</p>
            <h1 className="mt-2 font-serif text-4xl leading-tight text-[#211d19] md:text-5xl">{couple}</h1>
            <p className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-[#4a4239]">
              <span className="flex items-center gap-1.5 capitalize"><Calendar className="h-4 w-4 text-[#5f7d68]" aria-hidden="true" />{formatLongDate(data.wedding?.date)}</span>
              {data.wedding?.venue ? <span className="flex items-center gap-1.5"><MapPin className="h-4 w-4 text-[#5f7d68]" aria-hidden="true" />{data.wedding.venue}</span> : null}
            </p>
            {data.collaborator?.role ? <div className="mt-4"><Pill tone="info">{data.collaborator.role}</Pill></div> : null}
          </div>
          <div className="flex min-w-[170px] shrink-0 flex-col items-center rounded-2xl border border-[#d9e3d6] bg-white/80 px-6 py-5 text-center shadow-sm">
            <span className="font-serif text-4xl text-[#3f5a48]" aria-label="Compte à rebours">{countdown.big}</span>
            <span className="mt-1 text-xs font-semibold uppercase tracking-wider text-[#5b5247]">{countdown.small}</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard label="Missions à suivre" value={tasks.length} hint={tasks.length ? "Assignées à vous" : "Rien d'urgent"} tone={tasks.length ? "warning" : "neutral"} icon={Briefcase} />
        <StatCard label="Prochains jalons" value={events.length} hint="Dans le planning" icon={Calendar} />
        {canSeeGuests ? (
          guestStats
            ? <StatCard label="Invités confirmés" value={`${guestStats.confirmed}/${guestStats.total}`} hint={`${guestStats.responseRate} % ont répondu`} tone="success" icon={CheckCircle2} />
            : <SkeletonBlock className="h-[104px]" />
        ) : null}
        {canSeeGuests && guestStats ? (
          <StatCard label="À relancer" value={guestStats.pending} hint={guestStats.pending ? "Sans réponse" : "Tout le monde a répondu"} tone={guestStats.pending ? "warning" : "success"} icon={Users} />
        ) : null}
      </div>

      <div className="grid gap-6 lg:grid-cols-5">
        <Card className="lg:col-span-3">
          <div className="mb-4 flex items-center justify-between gap-3">
            <h2 className="font-serif text-xl text-[#211d19]">Missions prioritaires</h2>
            {permissions.mission ? <button type="button" onClick={() => onNavigate("mission")} className="flex items-center gap-1 text-xs font-semibold text-[#5f7d68] hover:underline">Tout voir <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" /></button> : null}
          </div>
          {tasks.length === 0 ? (
            <EmptyState icon={CheckCircle2} title="Aucune mission prioritaire" text="Les missions qui vous sont confiées apparaîtront ici." />
          ) : (
            <ul className="space-y-3">
              {tasks.map((task) => {
                const state = STATUS[task.status || "todo"] || STATUS.todo;
                return (
                  <li key={task.id} className="rounded-2xl border border-[#efe8da] bg-[#fbf8f2] p-4">
                    <div className="flex items-start justify-between gap-3">
                      <p className="min-w-0 break-words font-semibold text-[#211d19]">{task.title}</p>
                      <Pill tone={state.tone}>{state.label}</Pill>
                    </div>
                    <p className="mt-2 flex flex-wrap items-center gap-2 text-xs text-[#5b5247]">
                      <span>{task.category || "Autre"}</span>
                      <Pill tone={PRIORITY_TONE[task.priority] || "neutral"}>{task.priority || "Moyenne"}</Pill>
                    </p>
                  </li>
                );
              })}
            </ul>
          )}
        </Card>

        <Card className="lg:col-span-2">
          <div className="mb-4 flex items-center justify-between gap-3">
            <h2 className="font-serif text-xl text-[#211d19]">Prochains jalons</h2>
            {permissions.timeline ? <button type="button" onClick={() => onNavigate("timeline")} className="flex items-center gap-1 text-xs font-semibold text-[#5f7d68] hover:underline">Planning <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" /></button> : null}
          </div>
          {events.length === 0 ? (
            <EmptyState icon={Calendar} title="Aucun jalon" text="Le planning partagé n'a pas encore d'événement." />
          ) : (
            <ol className="relative space-y-4 border-l-2 border-[#e3ece0] pl-5">
              {events.map((event) => (
                <li key={event.id} className="relative">
                  <span className="absolute -left-[27px] top-1.5 h-3 w-3 rounded-full border-2 border-white bg-[#5f7d68]" aria-hidden="true" />
                  <p className="text-xs font-bold tracking-wider text-[#5f7d68]">{event.time || "À planifier"}</p>
                  <p className="font-semibold text-[#211d19]">{event.title}</p>
                  {event.description ? <p className="mt-0.5 text-xs text-[#5b5247]">{event.description}</p> : null}
                </li>
              ))}
            </ol>
          )}
        </Card>
      </div>

      {shortcuts.length ? (
        <div>
          <h2 className="mb-3 font-serif text-xl text-[#211d19]">Accès rapide</h2>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
            {shortcuts.map((entry) => {
              const Icon = entry.icon;
              return (
                <button key={entry.id} type="button" onClick={() => onNavigate(entry.id)} className="group flex items-center gap-3 rounded-2xl border border-[#e9e1d4] bg-white p-4 text-left shadow-sm transition-all hover:-translate-y-0.5 hover:border-[#bfd0bb] hover:shadow-md">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#eef3ec] text-[#3f5a48]"><Icon className="h-5 w-5" aria-hidden="true" /></span>
                  <span className="min-w-0">
                    <span className="block text-sm font-semibold text-[#211d19]">{entry.label}</span>
                    <span className="block truncate text-xs text-[#6a6055]">{entry.text}</span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
}
